import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Eye, EyeOff, Check, Sun, Moon, MonitorSmartphone } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { toast } from "sonner";

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const cycle = () => {
    const order = ["system", "light", "dark"];
    setTheme(order[(order.indexOf(theme) + 1) % order.length]);
  };
  const Icon = theme === "dark" ? Moon : theme === "light" ? Sun : MonitorSmartphone;
  const label = theme === "dark" ? "Dark" : theme === "light" ? "Light" : "Auto";
  return (
    <button onClick={cycle} className="nl-theme-toggle font-mono" data-testid="theme-toggle" title={`Theme: ${label}`}>
      <Icon size={14} /> {label}
    </button>
  );
}

function PasswordStrength({ password }) {
  const checks = [
    { label: "6+ characters", pass: password.length >= 6 },
    { label: "Uppercase", pass: /[A-Z]/.test(password) },
    { label: "Number", pass: /\d/.test(password) },
  ];
  if (!password) return null;
  return (
    <div className="nl-pw-strength">
      {checks.map((c) => (
        <span key={c.label} className={`nl-pw-check${c.pass ? " nl-pw-pass" : ""}`}>
          {c.pass && <Check size={10} />} {c.label}
        </span>
      ))}
    </div>
  );
}

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name || !email || !password) { setError("All fields are required."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created!");
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed.");
    } finally { setLoading(false); }
  };

  return (
    <div className="nl-page" data-testid="register-page">
      <div className="nl-grid" aria-hidden="true" />
      <div className="nl-scan" aria-hidden="true" />

      {/* Left branding panel */}
      <div className="nl-left">
        <div className="nl-brand">
          <div className="nl-logo-mark" />
          <div>
            <div className="nl-logo-name font-heading">Niyant</div>
            <div className="nl-logo-ver font-mono">v2.4 · production</div>
          </div>
        </div>

        <div className="nl-hero">
          <p className="nl-hero-tag font-mono">Start in 60 seconds</p>
          <h1 className="nl-hero-h1 font-heading" data-testid="register-title">
            Claim<br />Your<br /><span className="nl-accent-text">Cloud.</span>
          </h1>
          <div className="nl-hero-desc font-mono">
            One platform for discovery, governance<br />
            and cost intelligence.<br />
            <strong>Free tier. No credit card.</strong>
          </div>
        </div>

        <div className="nl-status font-mono">
          <span className="nl-status-item"><span className="nl-dot" />All systems operational</span>
        </div>
      </div>

      {/* Right sign-up panel */}
      <div className="nl-right">
        <ThemeToggle />
        <div className="nl-form-wrap">
          <p className="nl-form-tag font-mono">// new operator</p>
          <h2 className="nl-form-title font-heading">Create account</h2>
          <p className="nl-form-sub font-mono">Azure resource discovery starts here</p>

          {error && <div className="nl-error font-mono" data-testid="register-error">{error}</div>}

          <form onSubmit={handleSubmit} className="nl-form">
            <div className="nl-field">
              <label className="nl-label font-mono">Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Your full name" autoComplete="name"
                className="nl-input font-mono" data-testid="register-name-input" />
            </div>

            <div className="nl-field">
              <label className="nl-label font-mono">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@yourorg.com" autoComplete="email"
                className="nl-input font-mono" data-testid="register-email-input" />
            </div>

            <div className="nl-field">
              <label className="nl-label font-mono">Password</label>
              <div className="nl-pw-wrap">
                <input type={showPassword ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters"
                  autoComplete="new-password" className="nl-input font-mono"
                  data-testid="register-password-input" />
                <button type="button" className="nl-pw-toggle font-mono"
                  onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <PasswordStrength password={password} />
            </div>

            <button type="submit" disabled={loading}
              className={`nl-btn-primary${loading ? " nl-loading" : ""}`}
              data-testid="register-submit-button">
              <span>{loading ? "" : "Create Account"}</span>
            </button>
          </form>

          <div className="nl-footer">
            <span className="nl-footer-text font-mono">&copy; 2026 Niyant · Go Build</span>
            <span className="nl-motto font-mono">Go Build</span>
          </div>

          <p className="nl-register font-mono">
            Already have an account? <Link to="/login" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
